import React, { useState } from "react";
import { Card, Rate, Input, Button, Space, Typography, Tag } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { verifyVisit } from "../network/api";
import { useAuth } from "../context/AuthContext";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import { VISIT_STATUS } from "../constants/visits";

const { Text, Paragraph } = Typography;

// D3 — supervisor verifies a completed visit + rates the technician's work.
export default function SupervisorReview({ visit, onChange }) {
  const { isSupervisor } = useAuth(); // supervisor+ (incl. hardwareAdmin/superAdmin)
  const [rating, setRating] = useState(visit.rating || 0);
  const [comment, setComment] = useState(visit.ratingComment || "");
  const [saving, setSaving] = useState(false);

  // Verification only applies once the work is done.
  if (visit.status !== VISIT_STATUS.COMPLETED) return null;
  const verified = !!visit.verifiedAt;

  const submit = async () => {
    setSaving(true);
    try {
      await verifyVisit(visit._id, { rating: rating || undefined, comment });
      notify.success(verified ? "Review updated" : "Visit verified");
      onChange?.();
    } catch (e) {
      notify.error(serverError(e, "Failed to verify visit"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Supervisor Review">
      {verified && (
        <Space wrap style={{ marginBottom: isSupervisor ? 14 : 0 }}>
          <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Verified</Tag>
          {visit.rating ? <Rate disabled value={visit.rating} /> : null}
          <Text type="secondary">by {visit.verifiedByName || "supervisor"} · {relativeTime(visit.verifiedAt)}</Text>
        </Space>
      )}

      {isSupervisor ? (
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <Space>
            <Text>Technician rating:</Text>
            <Rate value={rating} onChange={setRating} />
          </Space>
          <Input.TextArea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment on the work" />
          <Button type="primary" loading={saving} onClick={submit}>
            {verified ? "Update review" : "Verify visit"}
          </Button>
        </Space>
      ) : verified ? (
        visit.ratingComment ? <Paragraph style={{ marginBottom: 0 }}>{visit.ratingComment}</Paragraph> : null
      ) : (
        <Text type="secondary">Awaiting supervisor verification.</Text>
      )}
    </Card>
  );
}
